import api from './api';
import { paymentService } from './paymentService';

class MessageService {
    async getConversations(page = 0, size = 20) {
        try {
            const response = await api.get(`/conversations`, {
                params: { page, size }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching conversations:', error);
            throw error;
        }
    }

    /**
     * Search the current user's conversations by the other person's name or by message text.
     * Returns a page of conversations; each result may carry matchedMessageText/matchedMessageAt.
     * Pass an AbortSignal to cancel a search that is no longer wanted.
     */
    async searchConversations(query, page = 0, size = 20, signal) {
        try {
            const response = await api.get(`/conversations/search`, {
                params: { q: query, page, size },
                signal
            });
            return response.data;
        } catch (error) {
            if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
                console.error('Error searching conversations:', error);
            }
            throw error;
        }
    }

    async getMessages(conversationId, page = 0, size = 20) {
        try {
            const response = await api.get(`/conversations/${conversationId}/messages`, {
                params: { 
                    page, 
                    size,
                    sort: 'createdAt,desc'
                }
            });
            return response.data.content || [];
        } catch (error) {
            console.error('Error fetching messages:', error);
            throw error;
        }
    }

    async getMessage(conversationId, messageId) {
        try {
            const response = await api.get(`/conversations/${conversationId}/messages/${messageId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching single message:', error);
            throw error;
        }
    }

    async sendMessage(conversationId, messageData) {
        try {
            const response = await api.post(`/conversations/${conversationId}/messages`, messageData);
            return response.data;
        } catch (error) {
            console.error('Error sending message:', error);
            
            // Check for specific error cases
            if (error.response?.status === 403) {
                throw new Error('You do not have permission to send messages in this conversation');
            }
            if (error.response?.status === 404) {
                throw new Error('Conversation not found');
            }
            
            throw error;
        }
    }

    async createPaymentLink(messageId) {
        // Delegate to paymentService for consistency
        return paymentService.createMessagePaymentLink(messageId);
    }

    async checkPaymentStatus(paymentId) {
        // Delegate to paymentService for consistency
        return paymentService.checkPaymentStatus(paymentId);
    }

    async markMessagesAsRead(conversationId) {
        try {
            const response = await api.put(`/conversations/${conversationId}/messages/read`);
            return response.data;
        } catch (error) {
            console.error('Error marking messages as read:', error);
            throw error;
        }
    }

    async createConversation(recipientId, initialMessage) {
        try {
            const response = await api.post(`/conversations`, {
                recipientId,
                message: initialMessage
            });
            return response.data;
        } catch (error) {
            console.error('Error creating conversation:', error);
            throw error;
        }
    }

    /**
     * Create or get existing direct conversation with another user
     * @param {string} recipientId - UUID of the recipient
     * @returns {Promise<Object>} - Conversation DTO with canMessage/isReadOnly flags
     * @throws {Error} - SUBSCRIPTION_REQUIRED if no active subscription exists
     */
    async createDirectConversation(recipientId) {
        try {
            const response = await api.post(`/conversations/direct`, null, {
                params: { recipientId }
            });
            return response.data;
        } catch (error) {
            console.error('Error creating direct conversation:', error);
            
            // Check for subscription required error
            if (error.response?.status === 403) {
                const message = error.response?.data?.message || error.response?.data;
                if (message === 'SUBSCRIPTION_REQUIRED') {
                    const subscriptionError = new Error('SUBSCRIPTION_REQUIRED');
                    subscriptionError.code = 'SUBSCRIPTION_REQUIRED';
                    throw subscriptionError;
                }
                if (message?.includes('blocked')) {
                    const blockedError = new Error('USER_BLOCKED');
                    blockedError.code = 'USER_BLOCKED';
                    throw blockedError;
                }
            }
            
            throw error;
        }
    }

    /**
     * Resolve (or create) the DM conversation with the globally configured
     * Support User. Returns the ConversationDTO so the caller can navigate
     * to /messages/{conversationId}. Throws SUPPORT_UNAVAILABLE if the backend
     * has no support user configured.
     */
    async getSupportConversation() {
        try {
            const response = await api.get(`/conversations/support`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                const err = new Error('SUPPORT_UNAVAILABLE');
                err.code = 'SUPPORT_UNAVAILABLE';
                throw err;
            }
            console.error('Error opening support conversation:', error);
            throw error;
        }
    }

    // ==================== MESSAGE MENU ACTIONS ====================

    async deleteMessage(conversationId, messageId) {
        try {
            await api.delete(`/conversations/${conversationId}/messages/${messageId}`);
            return true;
        } catch (error) {
            console.error('Error deleting message:', error);
            
            if (error.response?.status === 403) {
                throw new Error('You can only delete your own messages');
            }
            if (error.response?.status === 404) {
                throw new Error('Message not found');
            }
            
            throw error;
        }
    }

    async replyToMessage(conversationId, replyToMessageId, messageData) {
        try {
            const replyData = {
                content: messageData.html || messageData.text,
                mediaItems: messageData.mediaItems?.map(item => ({
                    fileName: item.fileName,      // Fixed: changed from 'name' to 'fileName' to match backend MediaDTO
                    mimeType: item.mimeType,      // Fixed: changed from 'type' to 'mimeType' to match backend MediaDTO
                    mediaType: item.mediaType,
                    s3Key: item.s3Key || item.url || item.preview, // Prefer s3Key from presigned upload
                    isFreePreview: item.isFreePreview,
                    // Include dimensions for video transcoding aspect ratio preservation
                    width: item.width || null,
                    height: item.height || null,
                    // Convert milliseconds to seconds (Lambda returns ms, backend expects seconds)
                    duration: item.duration ? Math.round(item.duration / 1000) : null
                })) || [],
                isPaid: messageData.hasPaidContent || messageData.messagePrice > 0,
                price: messageData.messagePrice || 0,
                replyToMessageId: replyToMessageId
            };
            
            const response = await api.post(`/conversations/${conversationId}/messages/reply`, replyData);
            return response.data;
        } catch (error) {
            console.error('Error sending reply:', error);
            
            if (error.response?.status === 403) {
                throw new Error('You do not have permission to reply in this conversation');
            }
            if (error.response?.status === 404) {
                throw new Error('Original message not found');
            }
            
            throw error;
        }
    }

    // ==================== REACTION METHODS ====================

    async addReaction(conversationId, messageId, emoji) {
        try {
            const response = await api.post(`/conversations/${conversationId}/messages/${messageId}/reactions`, {
                conversationId: conversationId,
                messageId: messageId,
                action: "add",
                emoji: emoji
            });
            return response.data;
        } catch (error) {
            console.error('Error adding reaction:', error);
            
            if (error.response?.status === 403) {
                throw new Error('You do not have permission to react in this conversation');
            }
            if (error.response?.status === 404) {
                throw new Error('Message not found');
            }
            
            throw error;
        }
    }

    async removeReaction(conversationId, messageId, emoji) {
        try {
            const payload = {
                conversationId: conversationId,
                messageId: messageId,
                action: "remove"
            };
            
            // Include emoji if provided (for specific emoji removal)
            if (emoji) {
                payload.emoji = emoji;
            }
            
            const response = await api.post(`/conversations/${conversationId}/messages/${messageId}/reactions`, payload);
            
            return response.data;
        } catch (error) {
            console.error('Error removing reaction:', error);
            
            if (error.response?.status === 403) {
                throw new Error('You do not have permission to remove reactions in this conversation');
            }
            if (error.response?.status === 404) {
                throw new Error('Message or reaction not found');
            }
            
            throw error;
        }
    }

    async getMessageReactions(conversationId, messageId) {
        try {
            
            const response = await api.get(`/conversations/${conversationId}/messages/${messageId}/reactions`);
            
            return response.data;
        } catch (error) {
            console.error('Error getting message reactions:', error);
            
            if (error.response?.status === 403) {
                throw new Error('You do not have permission to view reactions in this conversation');
            }
            if (error.response?.status === 404) {
                throw new Error('Message not found');
            }
            
            throw error;
        }
    }

    /**
     * Get total unread message count across all conversations
     * Used to display badge on Messages icon in navigation
     */
    async getTotalUnreadCount() {
        try {
            const response = await api.get('/conversations/unread-count');
            return response.data.totalUnread;
        } catch (error) {
            console.error('Error fetching total unread count:', error);
            return 0; // Return 0 on error to avoid breaking the UI
        }
    }

    /**
     * Get all media attachments in a conversation (paginated with filters)
     * Returns images and videos with unlock status
     */
    async getConversationMedia(conversationId, page = 0, size = 20, lockFilter = 'all', typeFilter = 'all') {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                size: size.toString()
            });
            if (lockFilter !== 'all') params.append('lockFilter', lockFilter);
            if (typeFilter !== 'all') params.append('typeFilter', typeFilter);
            
            const response = await api.get(`/conversations/${conversationId}/media?${params.toString()}`);
            return response;
        } catch (error) {
            console.error('Error fetching conversation media:', error);
            throw error;
        }
    }

    // ---- Scheduled Messages ----
    async getScheduledMessages() {
        try {
            const response = await api.get('/conversations/messages/scheduled');
            return response.data;
        } catch (error) {
            console.error('Error fetching scheduled messages:', error);
            throw error;
        }
    }

    async cancelScheduledMessage(conversationId, messageId) {
        try {
            await api.delete(`/conversations/${conversationId}/messages/${messageId}/schedule`);
        } catch (error) {
            console.error('Error cancelling scheduled message:', error);
            throw error;
        }
    }

    async rescheduleMessage(conversationId, messageId, scheduledFor) {
        try {
            const response = await api.patch(`/conversations/${conversationId}/messages/${messageId}/schedule`, { scheduledFor });
            return response.data;
        } catch (error) {
            console.error('Error rescheduling message:', error);
            throw error;
        }
    }

    async updateScheduledMessage(conversationId, messageId, body) {
        try {
            // body: { content?, mediaItems?, scheduledFor? }
            const response = await api.patch(`/conversations/${conversationId}/messages/${messageId}/content`, body);
            return response.data;
        } catch (error) {
            console.error('Error updating scheduled message:', error);
            throw error;
        }
    }

    /**
     * Report a direct message for moderation review. Backed by the polymorphic
     * `content_reports` table on the server. Rejects with a normalized error
     * object; callers should surface `err.message` (and treat HTTP 409 as
     * "already reported").
     *
     * @param {string} messageId  The message UUID (not the conversation).
     */
    async reportMessage(messageId, { reason, note } = {}) {
        try {
            const response = await api.post(`/messages/${messageId}/report`, { reason, note });
            return response.data;
        } catch (error) {
            const payload = error.response?.data || { message: error.message || 'Could not submit report' };
            payload.status = error.response?.status;
            throw payload;
        }
    }
}

export const messageService = new MessageService();